'use client';

/**
 * OperatorDashboard — bespoke body for the "Operator" persona.
 *
 * Operators are mission-driven and live for the game layer: rank, XP
 * (Intel), streak, the active operation, and territory. They open the
 * app asking: where am I in the progression, am I cleared to deploy,
 * what's today's mission. So the body leads with:
 *
 *   1. PROGRESSION HERO — rank/title + level bar + streak + operation
 *      goal + equipped archetype. The "war room" spine that makes the
 *      grind legible at a glance.
 *   2. GO-light readiness banner (recovery folded in).
 *   3. "Deploy" CTA (shared StartWorkoutCard).
 *
 * Operations / territory / weekly missions / week schedule then come
 * from the shared tail, which app/page.tsx still renders for Operator.
 * Nutrition is off for this persona by default.
 */

import { useEffect, useState } from 'react';
import { Flame, Crosshair, Shield } from 'lucide-react';
import { displayReadiness } from '@/lib/unifiedIntelligence';
import StartWorkoutCard from '@/components/dashboard/StartWorkoutCard';
import type { DashboardData } from '@/lib/hooks/useDashboardData';
import type { DBProgressionState } from '@/lib/db';

function goLight(readiness: number | null | undefined): {
  status: string; sub: string; tw: string; ring: string; glow: string;
} {
  if (readiness == null || readiness <= 0) {
    return { status: 'STANDBY', sub: 'No recovery signal — log a check-in', tw: 'text-slate-400', ring: 'border-slate-600', glow: 'shadow-none' };
  }
  if (readiness >= 70) return { status: 'GO', sub: `Cleared to deploy · readiness ${Math.round(readiness)}`, tw: 'text-emerald-400', ring: 'border-emerald-500/50', glow: 'shadow-[0_0_30px_-10px_rgba(74,222,128,0.6)]' };
  if (readiness >= 40) return { status: 'CAUTION', sub: `Deploy with control · readiness ${Math.round(readiness)}`, tw: 'text-amber-400', ring: 'border-amber-500/50', glow: 'shadow-[0_0_30px_-10px_rgba(245,158,11,0.6)]' };
  return { status: 'STAND DOWN', sub: `Recover before you push · readiness ${Math.round(readiness)}`, tw: 'text-red-400', ring: 'border-red-500/50', glow: 'shadow-[0_0_30px_-10px_rgba(248,113,113,0.6)]' };
}

interface HeroModel {
  title: string;
  rank: string;
  level: number;
  levelPct: number;       // 0-100 progress to next level
  levelIntel: number;
  levelNeed: number;
  weeklyIntel: number;    // intel earned this ISO week — momentum signal
  streakDays: number;
  streakTitle: string;
  opName: string;
  opPct: number;          // 0-100 operation intel goal progress
  opEarned: number;
  opGoal: number;
  archetype: string | null;
}

export default function OperatorDashboard({ data }: { data: DashboardData }) {
  const { activeBreak } = data;
  // See displayReadiness — null when the number is the provisional constant.
  const readiness = displayReadiness(data.intelligenceReport);
  const g = goLight(readiness);

  const [hero, setHero] = useState<HeroModel | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!data.user?.uid) return;
      try {
        const [{ loadProgressionState }, { intelForLevel, getStreakTitle }, { ARCHETYPES }] = await Promise.all([
          import('@/lib/progressionService'),
          import('@/lib/xpSystem'),
          import('@/lib/archetypeSystem'),
        ]);
        const s: DBProgressionState = await loadProgressionState(data.user.uid);
        if (cancelled) return;
        const levelNeed = intelForLevel(s.level);
        const opGoal = s.operation?.intelGoal ?? 0;
        const opEarned = s.operation?.intelEarned ?? 0;
        setHero({
          title: s.rank?.title || 'Operator',
          rank: s.rank?.current || '',
          level: s.level,
          levelIntel: s.levelIntel,
          levelNeed,
          levelPct: levelNeed > 0 ? Math.min(100, Math.round((s.levelIntel / levelNeed) * 100)) : 0,
          weeklyIntel: s.weeklyIntel || 0,
          streakDays: s.streakDays || 0,
          streakTitle: getStreakTitle(s.streakDays || 0).title,
          opName: s.operation?.name || '',
          opEarned,
          opGoal,
          opPct: opGoal > 0 ? Math.min(100, Math.round((opEarned / opGoal) * 100)) : 0,
          archetype: s.equippedArchetype ? (ARCHETYPES[s.equippedArchetype]?.name || s.equippedArchetype) : null,
        });
      } catch (e) {
        console.warn('[operator-dash] progression load failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [data.user?.uid]);

  return (
    <div className="space-y-3">
      {/* PROGRESSION HERO — the war-room spine: rank, level, streak, op. */}
      {hero && (
        <button
          onClick={() => { window.location.href = '/barracks'; }}
          className="w-full text-left relative overflow-hidden rounded-xl border border-amber-500/40 bg-hud-ink/85 hover:border-amber-500/70 transition active:scale-[0.995]"
        >
          <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-40" />
          <header className="px-4 py-1.5 border-b border-slate-800/60 flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-slate-500">Operator File · SYS·OPF·01</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-amber-400/70">Barracks →</span>
          </header>

          {/* Identity + level */}
          <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-heading text-lg font-bold tracking-wide text-amber-400 truncate">{hero.title}</div>
              {hero.rank && <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">{hero.rank}</div>}
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading text-2xl font-bold tabular-nums text-slate-100 leading-none">LVL {hero.level}</div>
              <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-600 mt-1">{hero.levelIntel}/{hero.levelNeed} intel</div>
            </div>
          </div>

          {/* Level progress bar + weekly velocity + intel-to-next */}
          <div className="px-4 pb-3 space-y-1">
            <div className="relative h-1.5 rounded-sm bg-slate-900 border border-slate-800 overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-amber-400" style={{ width: `${hero.levelPct}%` }} />
            </div>
            <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">
              <span className={hero.weeklyIntel > 0 ? 'text-emerald-400' : 'text-slate-600'}>
                {hero.weeklyIntel > 0 ? '↑ ' : ''}{hero.weeklyIntel} intel · 7d
              </span>
              <span>
                {Math.max(0, hero.levelNeed - hero.levelIntel)} to LVL {hero.level + 1}
              </span>
            </div>
          </div>

          {/* Stat triad — streak / operation / archetype */}
          <div className="grid grid-cols-3 border-t border-slate-800/60 divide-x divide-slate-800/60">
            <div className="px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Flame className={`w-3 h-3 ${hero.streakDays > 0 ? 'text-orange-400' : 'text-slate-600'}`} />
                <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">Streak</span>
              </div>
              <div className={`font-heading text-base font-bold tabular-nums ${hero.streakDays > 0 ? 'text-orange-400' : 'text-slate-500'}`}>{hero.streakDays}d</div>
              <div className="text-[9px] text-slate-500 leading-tight truncate">{hero.streakTitle}</div>
            </div>
            <div className="px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Crosshair className="w-3 h-3 text-hud-cyan" />
                <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">Operation</span>
              </div>
              {hero.opGoal > 0 ? (
                <>
                  <div className="font-heading text-base font-bold tabular-nums text-hud-cyan">{hero.opPct}%</div>
                  <div className="text-[9px] text-slate-500 leading-tight truncate">{hero.opName || `${hero.opEarned}/${hero.opGoal}`}</div>
                </>
              ) : (
                <div className="text-[10px] text-slate-500 leading-tight pt-1">No active goal</div>
              )}
            </div>
            <div className="px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Shield className={`w-3 h-3 ${hero.archetype ? 'text-purple-400' : 'text-amber-400'}`} />
                <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">Archetype</span>
              </div>
              {hero.archetype ? (
                <div className="font-heading text-sm font-bold text-purple-300 leading-tight truncate pt-0.5">{hero.archetype}</div>
              ) : (
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-amber-400 leading-tight pt-1 font-bold">
                  Pick one →
                </div>
              )}
            </div>
          </div>
        </button>
      )}

      {/* GO-light readiness — the "am I cleared to deploy?" answer.
          Replaces the recovery card-stack for this persona. */}
      <section className={`relative overflow-hidden rounded-xl border ${g.ring} bg-hud-ink/85 ${g.glow}`}>
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30" />
        <div className="px-4 py-3 flex items-center gap-4">
          <div className={`w-3 h-3 rounded-full ${g.tw.replace('text-', 'bg-')} animate-hud-blink shrink-0`} aria-hidden />
          <div className="min-w-0">
            <div className={`font-heading text-2xl font-bold tracking-wide ${g.tw}`}>{g.status}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">{g.sub}</div>
          </div>
        </div>
      </section>

      {/* Deploy — the workout CTA, reframed. Shared logic, operator verb. */}
      {/* "Start Workout", not "Deploy" — plain English in buttons. */}
      {!activeBreak && <StartWorkoutCard data={data} label="Start Workout" />}

      {/* Operations / territory / missions / week + vitals come from the
          shared tail, which app/page.tsx still renders for Operator. */}
    </div>
  );
}
